import { createQueryApi } from '@/shared/features/query/infra/adapters/rtk.adapter';
import type { ReduxQueryClient } from '@/shared/features/query/domain/interfaces/QueryClient';
import type { WishlistMutationResponseDTO } from '../dto/wishlist.dto';
import type { WishlistState } from '../../domain/models/WishlistItem';
import type { WishlistRepository } from '../../domain/interfaces/WishlistRepository';
import { adaptWishlistMutationDtoToState } from '../mappers/wishlist.mapper';
import type { AppDispatch } from '@/app/store/store';

const _wishlistApi = createQueryApi({ clientId: 'wishlistApi' });

const wishlistRtkApi = _wishlistApi.injectEndpoints({
  endpoints: (builder) => ({
    addToWishlist: builder.mutation<WishlistMutationResponseDTO, number>({
      query: (designId) => ({
        url: '/wishlist/addProduct',
        method: 'POST',
        data: `designId=${designId}`,
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        withCredentials: true,
      }),
    }),
    removeFromWishlist: builder.mutation<WishlistMutationResponseDTO, number>({
      query: (designId) => ({
        url: `/wishlist/removeItem?designId=${designId}`,
        method: 'DELETE',
        withCredentials: true,
      }),
    }),
  }),
});

export const wishlistApiRedux = _wishlistApi as unknown as ReduxQueryClient;

// eslint-disable-next-line @typescript-eslint/no-explicit-any -- RTK Query hooks types are complex
const hooks = wishlistRtkApi as any;
export const useAddToWishlistMutation: () => ReturnType<typeof hooks.useAddToWishlistMutation> =
  () => hooks.useAddToWishlistMutation();
export const useRemoveFromWishlistMutation: () => ReturnType<typeof hooks.useRemoveFromWishlistMutation> =
  () => hooks.useRemoveFromWishlistMutation();

export class WishlistAdapter implements WishlistRepository {
  constructor(private readonly dispatch: AppDispatch) {}

  async addToWishlist(designId: number): Promise<WishlistState> {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- RTK mutation result typing
    const result = await (this.dispatch as any)(
      wishlistRtkApi.endpoints.addToWishlist.initiate(designId)
    );
    if ('error' in result) throw result.error;
    return adaptWishlistMutationDtoToState(result.data as WishlistMutationResponseDTO);
  }

  async removeFromWishlist(designId: number): Promise<WishlistState> {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- RTK mutation result typing
    const result = await (this.dispatch as any)(
      wishlistRtkApi.endpoints.removeFromWishlist.initiate(designId)
    );
    if ('error' in result) throw result.error;
    return adaptWishlistMutationDtoToState(result.data as WishlistMutationResponseDTO);
  }
}
